import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Tabs, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import { fetchVenue } from '../api/fetchVenue.js';
import { fetchVenueEvents, fetchVenueParticipants, fetchVenueStats } from '../api/fetchVenueDetails.js';
import ProfileImage from '../components/ProfileImage.js';

function VenueDetails() {
    const auth = useAuth();
    const navigate = useNavigate();
    const { venue_id } = useParams();

    const [venue, setVenue] = useState(null);
    const [events, setEvents] = useState(null);
    const [participants, setParticipants] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadVenueData = useCallback(async () => {
        try {
            setLoading(true);
            const [venueData, eventsData, participantsData, statsData] = await Promise.all([
                fetchVenue(venue_id),
                fetchVenueEvents(venue_id),
                fetchVenueParticipants(venue_id),
                fetchVenueStats(venue_id),
            ]);

            setVenue(venueData);
            setEvents(eventsData);
            setParticipants(participantsData);
            setStats(statsData);
        } catch (err) {
            console.error('Error loading venue data:', err);
        } finally {
            setLoading(false);
        }
    }, [venue_id]);

    useEffect(() => {
        loadVenueData();
    }, [loadVenueData]);

    const handleBackToVenues = () => {
        navigate('/admin/venues');
    };

    const handleViewEvent = (event) => {
        navigate(`/admin/event/${event.event_id}`);
    };

    if (loading || !venue) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading venue details...</div>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <button
                    onClick={handleBackToVenues}
                    className="button is-light is-small"
                    style={{
                        marginBottom: '16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        padding: '6px 12px'
                    }}
                >
                    <ArrowLeftOutlined style={{ fontSize: '12px' }} />
                    Back to Venues
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    {venue.image_url ? (
                        <img
                            src={venue.image_url}
                            alt={venue.name}
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '8px',
                                objectFit: 'cover',
                                border: '1px solid #e5e7eb'
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '8px',
                            background: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '36px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📍
                        </div>
                    )}
                    <div>
                        <h1 style={{
                            fontSize: '28px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: 0,
                            marginBottom: '4px'
                        }}>
                            {venue.name}
                        </h1>
                        {venue.latitude && venue.longitude && (
                            <div style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                margin: 0
                            }}>
                                <a
                                    href={`https://www.google.com/maps/place/${venue.latitude},${venue.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#3b82f6',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '14px'
                                    }}
                                >
                                    📍 View {venue.name} on Google Maps
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Info Box */}
                <div style={{
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                }}>
                    <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: '16px', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                            <strong>Venues are organizational containers</strong> that help you view which events take place at which locations.
                            Participants are assigned to individual events, not directly to venues.
                            To add or remove participants, manage them through the individual event pages.
                        </div>
                    </div>
                </div>

                {/* Statistics */}
                {stats && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '24px'
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Total Events
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                                {stats.total_events || 0}
                            </div>
                        </div>
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Active Events
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#10b981' }}>
                                {stats.active_events || 0}
                            </div>
                        </div>
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Total Participants
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#3b82f6' }}>
                                {stats.total_participants || 0}
                            </div>
                        </div>
                        <div style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '16px'
                        }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Upcoming Sessions
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#f59e0b' }}>
                                {stats.upcoming_sessions || 0}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <Tabs
                defaultActiveKey="events"
                items={[
                    {
                        key: 'events',
                        label: `Events (${events?.length || 0})`,
                        children: (
                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                {events && events.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: '#f9fafb',
                                                borderBottom: '2px solid #e5e7eb'
                                            }}>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Event Name</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Group</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Status</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'right',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events.map((event) => (
                                                <tr
                                                    key={event.event_id}
                                                    style={{ borderBottom: '1px solid #e5e7eb' }}
                                                >
                                                    <td style={{ padding: '12px' }}>
                                                        <span style={{ fontWeight: '500', color: '#1f2937' }}>
                                                            {event.name}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#6b7280' }}>
                                                        {event.gathering?.name || '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            padding: '4px 10px',
                                                            borderRadius: '12px',
                                                            background: event.is_paused ? '#fef3c7' : '#d1fae5',
                                                            color: event.is_paused ? '#92400e' : '#065f46',
                                                            fontWeight: '600'
                                                        }}>
                                                            {event.is_paused ? 'Paused' : 'Active'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                                        <button
                                                            onClick={() => handleViewEvent(event)}
                                                            className="button is-small is-light"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{
                                        padding: '60px 20px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            No events at this venue yet
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            Events will appear here when they are assigned to this venue
                                        </div>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'participants',
                        label: `Participants (${participants?.length || 0})`,
                        children: (
                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                {participants && participants.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: '#f9fafb',
                                                borderBottom: '2px solid #e5e7eb'
                                            }}>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '40px'
                                                }}></th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Participant</th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Events</th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '70px'
                                                }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {participants.map((participant) => (
                                                <tr
                                                    key={participant.participant_id}
                                                    style={{
                                                        borderBottom: '1px solid #e8e8e8',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    <td style={{ padding: '3px 8px', width: '40px', verticalAlign: 'middle' }}>
                                                        <div style={{ width: '26px', height: '26px' }}>
                                                            <ProfileImage participant={participant} size={26} />
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{
                                                                fontWeight: '500',
                                                                color: '#1f2937',
                                                                fontSize: '12px'
                                                            }}>
                                                                {participant.name}
                                                            </span>
                                                            {participant.nickname && (
                                                                <span style={{
                                                                    color: '#6b7280',
                                                                    fontSize: '11px'
                                                                }}>
                                                                    "{participant.nickname}"
                                                                </span>
                                                            )}
                                                            {participant.email && (
                                                                <span style={{
                                                                    color: '#9ca3af',
                                                                    fontSize: '10px'
                                                                }}>
                                                                    {participant.email}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', width: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '1px 6px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#dbeafe',
                                                            color: '#1e40af',
                                                            fontSize: '10px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {participant.event_count} {participant.event_count === 1 ? 'event' : 'events'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', width: '70px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        {participant.active ? (
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                padding: '1px 6px',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#d1fae5',
                                                                color: '#065f46',
                                                                fontSize: '10px',
                                                                fontWeight: '500'
                                                            }}>
                                                                Active
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                padding: '1px 6px',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#fee2e2',
                                                                color: '#991b1b',
                                                                fontSize: '10px',
                                                                fontWeight: '500'
                                                            }}>
                                                                Inactive
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{
                                        padding: '60px 20px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            No participants at this venue yet
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            Participants are added through events at this venue
                                        </div>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}

export default VenueDetails;
